/**
 * Stammdaten des Objekts — was für alle seine Apartments gilt. Jede Karte bearbeitet in ihrem
 * Sheet genau ihren Teil; der Rest des Objekts geht unverändert mit (siehe objectUpdateRequest).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi, type PropertyGroupResponse } from '@/shared/api';
import { ObjectAccessCodeSection } from './ObjectAccessCodeSection';
import { ObjectAccessCodeSheet } from './ObjectAccessCodeSheet';
import { ObjectAddressCard } from './ObjectAddressCard';
import { ObjectAddressSheet } from './ObjectAddressSheet';
import { ObjectLocationCard } from './ObjectLocationCard';
import { ObjectStayTimesSection } from './ObjectStayTimesSection';
import { ObjectStayTimesSheet } from './ObjectStayTimesSheet';
import { useObjectDetail } from './ObjectDetailContext';

type Editing = 'address' | 'times' | 'code' | null;

export function ObjectStammdatenTab() {
  const { groupId } = useObjectDetail();
  const api = useApi();
  const { data: group } = useQuery({
    queryKey: ['property-groups', groupId],
    queryFn: () => api.propertyGroups.get(groupId),
  });
  if (!group) return null;
  return <ObjectStammdaten group={group} />;
}

function ObjectStammdaten({ group }: { group: PropertyGroupResponse }) {
  const { canEdit } = useObjectDetail();
  const [editing, setEditing] = useState<Editing>(null);
  const close = () => setEditing(null);

  return (
    <div className="@container space-y-5">
      <div>
        <h2 className="text-ink font-serif text-2xl">Stammdaten</h2>
        <p className="text-ink-soft mt-1 text-sm">Was für alle Apartments dieses Objekts gilt.</p>
      </div>
      <div className="@3xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] grid grid-cols-1 gap-5">
        <ObjectAddressCard
          group={group}
          onEdit={canEdit ? () => setEditing('address') : undefined}
        />
        <ObjectLocationCard group={group} canEdit={canEdit} />
      </div>
      <div className="@3xl:grid-cols-2 grid grid-cols-1 gap-5">
        <ObjectStayTimesSection
          group={group}
          canEdit={canEdit}
          onEdit={() => setEditing('times')}
        />
        <ObjectAccessCodeSection
          group={group}
          canEdit={canEdit}
          onEdit={() => setEditing('code')}
        />
      </div>
      {canEdit && editing === 'address' && <ObjectAddressSheet group={group} onClose={close} />}
      {canEdit && editing === 'times' && <ObjectStayTimesSheet group={group} onClose={close} />}
      {canEdit && editing === 'code' && <ObjectAccessCodeSheet group={group} onClose={close} />}
    </div>
  );
}
